import {
  invalidateAdminCompanyTreeQueryData,
  useGetApiAdminJobStatus,
  usePostAdminTriggersPackages,
  usePostApiAdminCompanyTreeSettingsBulkDelete,
} from '@sit/client-shared';
import { skipToken, useQueryClient } from '@tanstack/react-query';
import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button';
import Dialog from 'carbon-react/lib/components/dialog';
import {
  FlatTable,
  FlatTableBody,
  FlatTableCell,
  FlatTableCheckbox,
  FlatTableHead,
  FlatTableHeader,
  FlatTableRow,
} from 'carbon-react/lib/components/flat-table';
import Heading from 'carbon-react/lib/components/heading';
import Pod from 'carbon-react/lib/components/pod';
import Text from 'carbon-react/lib/components/typography';
import { useCallback, useEffect, useState } from 'react';
import toastr from '../../helpers/toastr';

interface BulkActionsProps {
  selectedCompanies: {
    id: number;
    externalId: string;
    syncEnd: string | null;
    syncStart: string | null;
    lastLoginAttempt?: string | null;
    tenantType: string | null;
  }[];
}

export function BulkActions({ selectedCompanies }: BulkActionsProps) {
  const companyTreeSettingsIds = selectedCompanies.map((c) => c.id);
  const disabled = companyTreeSettingsIds.length === 0;
  const queryClient = useQueryClient();
  const [jobId, setJobId] = useState<string | undefined>();
  const [deleteJobId, setDeleteJobId] = useState<string | undefined>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmations, setDeleteConfirmations] = useState<
    Record<number, boolean>
  >({});
  useGetApiAdminJobStatus(
    jobId
      ? {
          queryParams: {
            jobId,
            queueName: 'update-triggers-packages',
          },
          requestConfig: {
            skipToast: true,
          },
        }
      : skipToken,
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchInterval: 2_000,
      retry: (failureCount, error) => {
        if (error?.status === 404) {
          toastr.success('Update company triggers package job complete');
          setJobId(undefined);
          void invalidateAdminCompanyTreeQueryData(queryClient);
          return false;
        }
        if (!(error instanceof Error)) {
          return false;
        }
        if (error.message?.includes('Too many requests') || failureCount < 5) {
          return true;
        }
        toastr.error(error.message);
        return false;
      },
    },
  );
  const { mutate: updateTriggersPackages } = usePostAdminTriggersPackages();

  useGetApiAdminJobStatus(
    deleteJobId
      ? {
          queryParams: {
            jobId: deleteJobId,
            queueName: 'delete-company-tree',
          },
          requestConfig: {
            skipToast: true,
          },
        }
      : skipToken,
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchInterval: 2_000,
      retry: (failureCount, error) => {
        if (error?.status === 404) {
          toastr.success('Bulk delete job complete');
          setDeleteJobId(undefined);
          void invalidateAdminCompanyTreeQueryData(queryClient);
          return false;
        }
        if (!(error instanceof Error)) {
          return false;
        }
        if (error.message?.includes('Too many requests') || failureCount < 5) {
          return true;
        }
        toastr.error(error.message);
        return false;
      },
    },
  );

  const { mutate: bulkDeleteCompanies, isPending: isBulkDeletePending } =
    usePostApiAdminCompanyTreeSettingsBulkDelete();

  const startPackageUpdates = useCallback(() => {
    updateTriggersPackages(
      {
        body: {
          companyTreeSettingsIds,
        },
      },
      {
        onSuccess: (data) => {
          setJobId(data.jobId);
          toastr.info('Triggers package update started.');
        },
        onError: (error) => {
          toastr.error(error.message);
        },
      },
    );
  }, [companyTreeSettingsIds, updateTriggersPackages]);

  const startBulkDelete = useCallback(() => {
    bulkDeleteCompanies(
      {
        body: {
          companyTreeSettingsIds,
        },
      },
      {
        onSuccess: (data) => {
          setDeleteJobId(data.jobId);
          setDeleteDialogOpen(false);
          toastr.info('Bulk delete scheduled.');
        },
        onError: (error) => {
          const message =
            error instanceof Error ? error.message : 'Bulk delete failed';
          toastr.error(message);
        },
      },
    );
  }, [bulkDeleteCompanies, companyTreeSettingsIds]);

  const companyCount = companyTreeSettingsIds.length;
  const allCompaniesConfirmed =
    companyCount > 0 &&
    companyTreeSettingsIds.every((id) => deleteConfirmations[id]);

  useEffect(() => {
    if (!deleteDialogOpen) return;
    const confirmations = companyTreeSettingsIds.reduce(
      (acc, id) => {
        acc[id] = false;
        return acc;
      },
      {} as Record<number, boolean>,
    );
    setDeleteConfirmations(confirmations);
  }, [deleteDialogOpen, companyTreeSettingsIds]);

  return (
    <Box pt={2}>
      <Pod>
        <Heading headingType="h3" title="Bulk actions" />
        <Box display="flex" flexDirection="row" gap={2}>
          <Button
            disabled={disabled}
            buttonType="secondary"
            onClick={startPackageUpdates}
          >
            Update trigger package
          </Button>
          <Button
            disabled={disabled || !!deleteJobId}
            buttonType="secondary"
            destructive
            onClick={() => setDeleteDialogOpen(true)}
          >
            Bulk delete companies
          </Button>
        </Box>
      </Pod>
      <Dialog
        open={deleteDialogOpen}
        title="Bulk delete companies"
        onCancel={() => setDeleteDialogOpen(false)}
      >
        <Text>
          This will permanently delete {companyCount} compan
          {companyCount === 1 ? 'y' : 'ies'} and all associated data. This
          action cannot be undone.
        </Text>
        <Box mt={3} maxHeight="240px" overflowY="auto">
          <FlatTable colorTheme="transparent-white" hasStickyHead>
            <FlatTableHead>
              <FlatTableRow>
                <FlatTableHeader>Confirm</FlatTableHeader>
                <FlatTableHeader>Intacct Company ID</FlatTableHeader>
                <FlatTableHeader>Tenant Type</FlatTableHeader>
                <FlatTableHeader>Last Synced</FlatTableHeader>
                <FlatTableHeader>Last Login</FlatTableHeader>
                <FlatTableHeader>Company Tree ID</FlatTableHeader>
              </FlatTableRow>
            </FlatTableHead>
            <FlatTableBody>
              {selectedCompanies.map((company) => {
                const lastSync = company.syncEnd ?? company.syncStart ?? '—';
                const lastLogin = company.lastLoginAttempt ?? '—';
                const tenantType = company.tenantType ?? '—';

                return (
                  <FlatTableRow key={company.id}>
                    <FlatTableCheckbox
                      checked={!!deleteConfirmations[company.id]}
                      onChange={(ev) => {
                        setDeleteConfirmations((current) => ({
                          ...current,
                          [company.id]: ev.target.checked,
                        }));
                      }}
                    />
                    <FlatTableCell>{company.externalId}</FlatTableCell>
                    <FlatTableCell>{tenantType}</FlatTableCell>
                    <FlatTableCell>{lastSync}</FlatTableCell>
                    <FlatTableCell>{lastLogin}</FlatTableCell>
                    <FlatTableCell>{company.id}</FlatTableCell>
                  </FlatTableRow>
                );
              })}
            </FlatTableBody>
          </FlatTable>
        </Box>
        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            buttonType="secondary"
            onClick={() => setDeleteDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            buttonType="primary"
            destructive
            onClick={startBulkDelete}
            disabled={isBulkDeletePending || !allCompaniesConfirmed}
          >
            Delete
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
}
